import { useState, useEffect, useCallback, useRef } from 'react';
import { useCRM } from '../context/CRMContext';

const SYNC_POLL_MS = 45000;

/**
 * Hook to fetch leads from API with filters and pagination.
 * @param {Object} baseFilters - Default filters to always apply (e.g., { is_fresh: true })
 */
const useLeads = (baseFilters = {}) => {
  const { fetchLeads } = useCRM();
  const [leads, setLeads] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '', status: '', priority: '', source: '',
    service_type: '', property_type: '', project: '', assigned: '', assigned_to: '',
    form_name: '', date_from: '', date_to: '',
  });

  const currentPage = useRef(1);
  const currentLimit = useRef(25);

  const loadLeads = useCallback(async (overrideFilters, page, limit) => {
    setLoading(true);
    try {
      const f = overrideFilters || filters;
      const params = { ...baseFilters };

      params.page = page || currentPage.current;
      params.limit = limit || currentLimit.current;

      const csv = (v) => (Array.isArray(v) ? v.filter(Boolean).join(',') : v);

      if (f.search) params.search = f.search;
      if (csv(f.status)) params.statuses = csv(f.status);
      if (csv(f.priority)) params.priorities = csv(f.priority);
      if (csv(f.source)) params.sources = csv(f.source);
      if (csv(f.service_type)) params.service_types = csv(f.service_type);
      if (csv(f.property_type)) params.property_types = csv(f.property_type);
      if (csv(f.project)) params.projects = csv(f.project);
      if (f.assigned) params.assign_status = f.assigned;
      if (csv(f.assigned_to)) params.assigned_to = csv(f.assigned_to);
      if (csv(f.form_name)) params.form_name = csv(f.form_name);
      if (f.date_from) params.created_from = f.date_from;
      if (f.date_to) params.created_to = f.date_to;
      if (f.followup_from) params.followup_from = f.followup_from;
      if (f.followup_to) params.followup_to = f.followup_to;
      if (f.followup_filter) params.followup_filter = f.followup_filter;
      if (f.lead_type === 'fresh') params.is_fresh = true;
      else if (f.lead_type === 'imported') params.is_imported = true;

      const res = await fetchLeads(params);
      if (res.success) {
        setLeads(res.data?.leads || []);
        setPagination(res.data?.pagination || {});
      }
    } catch (err) {
      console.error('useLeads error:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchLeads, filters, baseFilters]);

  // Load on mount and when baseFilters change
  const baseKey = JSON.stringify(baseFilters);
  useEffect(() => {
    currentPage.current = 1;
    loadLeads(undefined, 1);
  }, [baseKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = useCallback((newFilters) => {
    currentPage.current = 1;
    loadLeads(newFilters, 1);
  }, [loadLeads]);

  const onPageChange = useCallback((page, limit) => {
    currentPage.current = page;
    if (limit) currentLimit.current = limit;
    loadLeads(filters, page, limit);
  }, [loadLeads, filters]);

  const refresh = useCallback(() => {
    loadLeads();
  }, [loadLeads]);

  // Keep a stable ref to the latest refresh so sync listeners don't re-subscribe on every filter change
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  // Cross-device sync: refetch on tab focus/visibility regain + poll every 45s while visible
  useEffect(() => {
    const doRefresh = () => refreshRef.current();
    const onFocus = () => doRefresh();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') doRefresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    const intervalId = setInterval(() => {
      if (document.hidden) return;
      doRefresh();
    }, SYNC_POLL_MS);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(intervalId);
    };
  }, []);

  return {
    leads,
    loading,
    pagination,
    filters,
    setFilters,
    onSearch,
    onPageChange,
    refresh,
  };
};

export default useLeads;
